/**
 * Windows desktop (Tauri) window-chrome bridge. The Windows build runs
 * borderless (decorations are switched off in the Rust shell) so the app
 * paints its own title bar; these wrappers drive the native window from the
 * webview. Every export no-ops outside the Windows Tauri runtime so callers
 * stay unconditional. macOS keeps its native traffic-light frame and must
 * never get this treatment.
 */
import { isTauriRuntime } from "./nativeShell";

export type WindowResizeDirection =
  | "East"
  | "North"
  | "NorthEast"
  | "NorthWest"
  | "South"
  | "SouthEast"
  | "SouthWest"
  | "West";

/** True only inside the Tauri webview running on Windows. */
export function isWindowsDesktop(): boolean {
  if (!isTauriRuntime()) return false;
  if (typeof navigator === "undefined") return false;
  // Tauri keeps the host OS in the UA string. macOS Tauri builds keep their
  // native frame, so the custom bar is gated to Windows only.
  return /Windows/i.test(navigator.userAgent);
}

async function currentWindow() {
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  return getCurrentWindow();
}

export async function winMinimize(): Promise<void> {
  if (!isWindowsDesktop()) return;
  try {
    await (await currentWindow()).minimize();
  } catch (error) {
    console.error("[WindowChrome] minimize failed", error);
  }
}

export async function winToggleMaximize(): Promise<void> {
  if (!isWindowsDesktop()) return;
  try {
    await (await currentWindow()).toggleMaximize();
  } catch (error) {
    console.error("[WindowChrome] toggleMaximize failed", error);
  }
}

export async function winClose(): Promise<void> {
  if (!isWindowsDesktop()) return;
  try {
    await (await currentWindow()).close();
  } catch (error) {
    console.error("[WindowChrome] close failed", error);
  }
}

export async function winStartDragging(): Promise<void> {
  if (!isWindowsDesktop()) return;
  try {
    await (await currentWindow()).startDragging();
  } catch (error) {
    console.error("[WindowChrome] startDragging failed", error);
  }
}

export async function winStartResize(direction: WindowResizeDirection): Promise<void> {
  if (!isWindowsDesktop()) return;
  try {
    await (await currentWindow()).startResizeDragging(direction);
  } catch (error) {
    console.error("[WindowChrome] resize failed", error);
  }
}

export async function winIsMaximized(): Promise<boolean> {
  if (!isWindowsDesktop()) return false;
  try {
    return await (await currentWindow()).isMaximized();
  } catch {
    return false;
  }
}

/**
 * Subscribe to native resize events so the maximize/restore glyph can track
 * the real window state (Aero Snap and the OS shortcut bypass our buttons).
 * Returns an unlisten function; no-op (returns a noop) off Windows.
 */
export async function onWindowMaximizeChange(
  handler: (maximized: boolean) => void,
): Promise<() => void> {
  if (!isWindowsDesktop()) return () => {};
  try {
    const win = await currentWindow();
    handler(await win.isMaximized());
    return await win.onResized(async () => {
      try {
        handler(await win.isMaximized());
      } catch {
        /* window torn down mid-event */
      }
    });
  } catch (error) {
    console.error("[WindowChrome] resize subscription failed", error);
    return () => {};
  }
}

/** Read whether the app is configured to launch at login. */
export async function winGetLaunchAtLogin(): Promise<boolean> {
  if (!isWindowsDesktop()) return false;
  try {
    const { isEnabled } = await import("@tauri-apps/plugin-autostart");
    return await isEnabled();
  } catch (error) {
    console.error("[WindowChrome] autostart read failed", error);
    return false;
  }
}

/** Toggle launch-at-login. Returns the resulting state. */
export async function winSetLaunchAtLogin(next: boolean): Promise<boolean> {
  if (!isWindowsDesktop()) return false;
  try {
    const { enable, disable, isEnabled } = await import(
      "@tauri-apps/plugin-autostart"
    );
    if (next) await enable();
    else await disable();
    return await isEnabled();
  } catch (error) {
    console.error("[WindowChrome] autostart toggle failed", error);
    return !next;
  }
}

/**
 * Flag <html> for the borderless Windows window frame (win-shell.css). The
 * solid chrome that goes with it is a wider, separate gate — see
 * navigation/solidChromeFlag.ts. Mirrors the data-mac-shell pattern.
 */
export function applyWindowsShellAttribute(): void {
  if (typeof document === "undefined") return;
  if (isWindowsDesktop()) {
    document.documentElement.setAttribute("data-win-shell", "true");
  }
}
